import { useCallback, useRef } from 'react';
import { pick, prop } from 'ramda';

import { dispatcher } from 'dispatcher';
import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useAppSelector, useUserSelector, useDeviceSelector } from 'js/store/hooks';

import { useOpenInbox } from '../../useOpenInbox';

const subscribe = (type) => {
    const api = window.Communication.API;

    dispatcher.dispatch(`${type}:loaded`);

    if (type === 'chat') {
        api.chat.onChatOpenWindow(() => {
            dispatcher.dispatch('chat:open');
        });

        api.chat.onAdminStatusChange(() => {
            dispatcher.dispatch('chat:AdminStatusChange');
        });
    } else if (type === 'inbox') {
        api.inbox.onOpenWindow(() => {
            dispatcher.dispatch('inbox:open');
        });

        api.inbox.onUpdateUnreadCount((count) => {
            dispatcher.dispatch('inbox:updateCounter', count);
            publicEventsAPIService.trigger('inbox:updateCounter', count);
        });
    }
};

const createContainer = (containerId, hideIcon, openInbox) => {
    const container = document.createElement('div');

    container.id = containerId;
    container.className = 'sticky';

    if (containerId === 'inboxContainer') {
        container.className += ' ignore-input';

        // Custom event handler to ensure opened inbox window on replies
        container.addEventListener('click', (event) => {
            const isCloseButton = event.target.classList.contains('app-react-components-Inbox-common-Notifications-index__close');
            const isNotificationClicked = event.target.closest('.mws-inbox-new-message-notification');

            if (isNotificationClicked && !isCloseButton || event.target.classList.contains('mws-inbox-header__btn--close')) {
                openInbox();
            }
        });
    }

    if (hideIcon) {
        container.className += ' hide-icon';
    }

    document.body.appendChild(container);
};

export const useInit = () => {
    const {
        casinoName,
        clientType = 'casino',
        clientVersion,
        inboxWidget,
        chatWidget
    } = useAppSelector(pick(['casinoName', 'clientType', 'clientVersion', 'inboxWidget', 'chatWidget']));
    const clientPlatform = useDeviceSelector(prop('clientPlatform'));
    const [dTagInboxShowMessageButton] = useDynamicTags([DYNAMIC_TAG_NAMES.inboxShowMessageButton]);
    const langRef = useRef(useUserSelector(prop('lang')));
    const openInbox = useOpenInbox();

    return useCallback((type) => {
        const widgetConfig = type === 'inbox' ? inboxWidget : chatWidget;
        const containerId = `${type}Container`;
        const hideIcon = !widgetConfig.showLaunchIcon;
        const initParams = {
            selector: `#${containerId}`,
            casinoName,
            clientSkin: casinoName,
            clientType,
            clientPlatform,
            clientVersion: clientVersion || '1.0',
            languageCode: langRef.current.slice(0, 2) || 'en',
            hideIcon,
            layout: widgetConfig.layout ?? {},
            ...type === 'inbox' && { layout: {
                common: {
                    showMinimizeIcon: true,
                    showOpenIcon: true
                },

                messagesList: {
                    order: 1,
                    rowCount: 100,
                    viewMode: 1,
                    activeTab: 0,
                    showNewMessageButton: dTagInboxShowMessageButton === 'true',
                    showDeleteIcon: true,
                    showReplyIcon: true
                },
                newMessage: {
                    category: 0,
                    subCategory: 1,
                    categories: ['support'],
                    subCategories: [['account', 'balance', 'gaming', 'bonus', 'other']]
                },
                messageDetails: {
                    showReplyButton: true
                }
            } }
        };

        createContainer(containerId, hideIcon, openInbox);
        subscribe(type);

        return window.Communication.API[type].init(initParams);
    }, [
        inboxWidget,
        chatWidget,
        casinoName,
        clientType,
        clientPlatform,
        clientVersion,
        dTagInboxShowMessageButton,
        openInbox
    ]);
};
